// ===== Preço de um produto numa tabela =====
//
// Migration 0058 · levantamento de 16/09 · decisões do Kaio no mesmo dia.
//
// A REGRA, num lugar só. Depois da 0058 a tabela de preço não guarda mais os
// preços: guarda um FATOR sobre a lista base. CORP 45 é `base × 1,45`.
//
// Por que isso é uma função e não uma multiplicação solta na tela: o mesmo
// número aparece na aba de tabela do negócio, na proposta e (quando chegar) no
// envio ao ERP. Três cópias da conta divergiriam no arredondamento antes de
// divergirem na regra — e divergência de centavo em preço vira discussão no
// fechamento.
//
// Sem dependência de banco nem de React, pela mesma razão de `dinheiro.js`:
// pode ser importado por qualquer lado sem esbarrar na regra de imports.

import { dinheiro } from './dinheiro.js';

/**
 * Preço de um produto numa tabela.
 *
 * Devolve `null` quando não há preço — e isso é diferente de zero. 48,3% do
 * catálogo só tem preço na "Tabela Setembro 2022", inativa no ERP, e por decisão
 * do Kaio esses produtos ENTRAM SEM PREÇO. Zero faria a proposta somar um item
 * de graça; `null` obriga a tela a dizer que não há preço.
 *
 * @param produto       objeto de `store.produtos` — usa `precoTabela` (a base) e `idProduto`
 * @param tabela        objeto de `store.tabelasPreco` — usa `tipo` e `fator`
 * @param itensManuais  linhas de `tabela_preco_itens`, só para tabela manual
 */
export function precoNaTabela(produto, tabela, itensManuais = []) {
  if (!produto) return null;

  // Sem tabela escolhida, vale a base. É o que o vendedor vê antes de vincular
  // cliente, e é honesto: a base é um preço real, não um palpite.
  if (!tabela) return precoBase(produto);

  if (tabela.tipo === 'manual') {
    // Manual tem preço próprio por produto e NÃO deriva de fator. `TABELA PAULA`
    // e `Especial Fortaleza` são as duas, com 31 itens ao todo.
    //
    // Casa por `produto_id` primeiro porque é a chave de verdade; cai no nome só
    // quando o item não tem produto vinculado (a coluna é anulável na 0004).
    const item = itensManuais.find((i) => (i.produto_id && i.produto_id === produto.id))
      || itensManuais.find((i) => norm(i.produto_nome) === norm(produto.nome));
    return item ? dinheiro(item.preco_tabela) : null;
  }

  const base = precoBase(produto);
  if (base == null) return null;

  // `fator` nulo numa tabela de fator é estado impossível — a restrição
  // `ck_tabelas_precos_fator` (0058) não deixa gravar. Se aparecer, é bug de
  // outro lugar, e devolver a base sem avisar esconderia. Devolve null.
  if (tabela.fator == null || !(tabela.fator > 0)) return null;

  return dinheiro(base * tabela.fator);
}

/** O preço da lista base, ou `null` quando o produto entrou sem preço. */
export function precoBase(produto) {
  const v = Number(produto?.precoTabela);
  return Number.isFinite(v) && v > 0 ? dinheiro(v) : null;
}

/**
 * Rótulo curto do que a tabela faz, para a tela não obrigar ninguém a decorar.
 * "×1,45" diz mais que "CORP 45" para quem está conferindo um preço.
 */
export function rotuloDaTabela(tabela) {
  if (!tabela) return '';
  if (tabela.tipo === 'manual') return 'preços próprios';
  if (tabela.fator == null) return '';
  if (Number(tabela.fator) === 1) return 'preço da base';
  return '×' + Number(tabela.fator).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

const norm = (s) => String(s || '').trim().toLowerCase();
